'use client';

import { getFragmentData } from '@packages/gql/generated/shopping';
import { RecoProductCardFragmentDoc } from '@packages/gql/generated/shopping/RecoProductCardFragmentDoc';
import { PrudsysBasketDocument } from '@packages/gql/generated/shopping/PrudsysBasketDocument';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../hooks/useRecoElement';
import type { ProductsRecoSliderProps, RdeType, RequestParams } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel';
import { RecoProductCard } from '../../RecoProductCard/RecoProductCard.client';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';

export type PrudsysBasketProductsRecoSliderProps = Pick<RequestParams, 'productIds'> &
  ProductsRecoSliderProps;

/* GraphQL */ `
  query PrudsysBasket($productIds: [String!]!, $locale: String!) {
    prudsysBasket(productIds: $productIds, locale: $locale) {
      title
      type
      recommendations {
        ...RecoProductCard
      }
      extendedViewUrl
    }
  }
`;

const RDE_TYPE: RdeType = 'prudsysBasket';

/**
 * PrudsysBasketProductsRecoSlider component description displayed in storybook
 * */
export const PrudsysBasketProductsRecoSlider: FC<PrudsysBasketProductsRecoSliderProps> = ({
  backgroundColor,
  productIds,
  fetchOnThreshold = false,
  onProductClick,
  onSeen,
  pageTemplate,
  preferTitle = false,
  title,
}) => {
  const { locale } = useIntl();
  const { isInThreshold, isInView, recoEl } = useRecoElement();

  const [{ data, error }] = useQuery({
    query: PrudsysBasketDocument,
    variables: {
      locale,
      productIds,
    },
    context: useMemo(
      () => ({ suspense: false, fetchOptions: { headers: { 'x-locale': locale } } }),
      [locale],
    ),
    pause: fetchOnThreshold ? !isInThreshold : false,
  });

  const recommendations = data ? data.prudsysBasket.recommendations : undefined;

  /** Filtered out recommendations that don't have a product (product === null) */
  const filteredRecommendations = recommendations
    ? recommendations.filter(
        (recommendation) => getFragmentData(RecoProductCardFragmentDoc, recommendation).product,
      )
    : undefined;

  const rdeTypeAbbr = data?.prudsysBasket.type;

  useProductsRecoTracking({
    pageTemplate,
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations: filteredRecommendations,
    title: data?.prudsysBasket.title || '',
    trackingCondition: isInView && !!filteredRecommendations && filteredRecommendations.length > 0,
    onSeen,
  });

  if (
    (error && !filteredRecommendations) ||
    (filteredRecommendations && filteredRecommendations.length === 0)
  ) {
    return null;
  }

  return (
    <RecoCarousel
      title={preferTitle ? title : data?.prudsysBasket.title || title}
      loadingState={!filteredRecommendations}
      backgroundColor={backgroundColor}
      ref={recoEl}
    >
      {filteredRecommendations?.map((recommendation, idx) => (
        <RecoProductCard
          // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          data={recommendation}
          onClick={(akl, sku) => {
            onProductClick?.(akl, sku);
          }}
          position={idx + 1}
          rdeTypeAbbr={rdeTypeAbbr}
        />
      ))}
    </RecoCarousel>
  );
};
